/**
 * Arrows for any horizontally scrolling track.
 *
 * The track is a plain scroll container -- no transforms, no index to keep in
 * sync -- so these buttons only nudge it and then read back where it landed.
 * Native scrolling and scroll-snap keep doing the work, which is why a swipe,
 * a trackpad, a keyboard and these arrows all agree.
 *
 * An arrow is shown only when there is something in that direction. That also
 * covers the non-carousel layouts: a grid never overflows, so neither arrow
 * ever appears and the element costs nothing.
 */
/*
 * Every carousel on a page asks to be re-checked at about the same moment --
 * when the page loads, when images arrive, when the window changes size. Done
 * one at a time, each check reads sizes (which makes the browser lay the page
 * out) and then shows or hides an arrow (which throws that layout away), so
 * the next carousel's read lays the page out all over again. A page with
 * sixteen carousels did that sixteen times. Requests are collected instead and
 * handled once per frame: every carousel is measured first, then every arrow
 * is set, so the page is laid out once.
 */
const pending = new Set();
let frame = 0;

/*
 * Past this many cards the dots stop fitting a small phone, so the progress
 * falls back to the continuous line.
 */
const MAX_DOTS = 12;

/**
 * Which of the track's cards are on screen, one entry per card the visitor
 * can see in the row. Cards hidden by CSS (a phone's cut-off, a feature tile
 * moved out of the row) have no boxes and are left out, so the dots count
 * what is actually there. A card counts as on screen when at least half of it
 * is inside the track. Reads only.
 *
 * @param {HTMLElement} track
 * @returns {boolean[]}
 */
function visibleCards(track) {
  const view = track.getBoundingClientRect();
  const flags = [];

  for (const card of track.children) {
    if (card.getClientRects().length === 0) continue;

    const box = card.getBoundingClientRect();
    if (box.width === 0) continue;

    const shown = Math.min(box.right, view.right) - Math.max(box.left, view.left);
    flags.push(shown >= box.width / 2);
  }

  return flags;
}

function flush() {
  frame = 0;
  const batch = [...pending].filter((carousel) => carousel.isConnected);
  pending.clear();

  const measured = batch.map((carousel) => carousel.measure());
  batch.forEach((carousel, i) => carousel.apply(measured[i]));
}

class CarouselArrows extends HTMLElement {
  connectedCallback() {
    // data-track names the scroller when it is drawn by markup that cannot
    // carry data-carousel-track itself (a slideshow snippet's <slideshow-slides>).
    this.track = this.querySelector(this.dataset.track || '[data-carousel-track]');
    this.prev = this.querySelector('[data-direction="prev"]');
    this.next = this.querySelector('[data-direction="next"]');
    this.progress = this.querySelector('[data-carousel-progress]');
    this.progressBar = this.progress?.querySelector('[data-carousel-progress-bar]');
    this.progressTrack = this.progress?.querySelector('[data-carousel-progress-track]');
    // Only present where the row asked for dots (see carousel-arrows.liquid).
    this.progressDots = this.progress?.querySelector('[data-carousel-progress-dots]');
    if (!this.track) return;

    // The snippet renders the line beside the arrows, ahead of the track;
    // it belongs under the row.
    if (this.progress) this.append(this.progress);

    this.update = this.update.bind(this);

    this.track.addEventListener('scroll', this.update, { passive: true });
    this.addEventListener('click', this.handleClick);

    // Cards start at whatever height the placeholder is and grow when the
    // images arrive, so a one-shot measurement on load would be wrong.
    this.observer = new ResizeObserver(this.update);
    this.observer.observe(this.track);

    // Cards added by script (the cart drawer's recommendations) can change
    // how far the row scrolls without changing its size.
    this.mutations = new MutationObserver(this.update);
    this.mutations.observe(this.track, { childList: true });

    this.update();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.update);
    pending.delete(this);
    this.removeEventListener('click', this.handleClick);
    this.observer?.disconnect();
    this.mutations?.disconnect();
  }

  handleClick = (event) => {
    const button = event.target.closest('[data-direction]');
    if (!button) return;

    const direction = button.dataset.direction === 'prev' ? -1 : 1;
    this.track.scrollBy({ left: this.step * direction, behavior: 'smooth' });
  };

  /**
   * One card plus the gap, so an arrow advances by exactly one card. A wide
   * tile (data-carousel-wide, e.g. a product section's feature tile) is
   * skipped when measuring: sized off it, every click would jump two cards.
   */
  get step() {
    const card =
      this.track.querySelector(':scope > :not([data-carousel-wide])') || this.track.firstElementChild;
    if (!card) return this.track.clientWidth;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  update() {
    pending.add(this);
    if (!frame) frame = requestAnimationFrame(flush);
  }

  // Reads only. Nothing here changes the page.
  measure() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // Card positions are only needed by a row that draws dots.
    const cards = this.progressDots && scrollWidth - clientWidth > 1 ? visibleCards(this.track) : [];
    return { scrollLeft, scrollWidth, clientWidth, cards };
  }

  // Writes only.
  apply({ scrollLeft, scrollWidth, clientWidth, cards }) {
    // Sub-pixel widths mean the end is rarely reached exactly.
    const max = scrollWidth - clientWidth;
    const scrollable = max > 1;

    if (this.prev) this.prev.hidden = !scrollable || scrollLeft <= 1;
    if (this.next) this.next.hidden = !scrollable || scrollLeft >= max - 1;

    if (this.progress) {
      this.progress.hidden = !scrollable;
      if (scrollable) this.applyProgress(scrollLeft, scrollWidth, clientWidth, cards);
    }
  }

  /**
   * One dot per card, lit while that card is on screen. A row without dots,
   * or too long for them, keeps the continuous line.
   */
  applyProgress(scrollLeft, scrollWidth, clientWidth, cards) {
    const useDots = Boolean(this.progressDots) && cards.length > 1 && cards.length <= MAX_DOTS;

    if (this.progressTrack) this.progressTrack.hidden = useDots;
    if (this.progressDots) this.progressDots.hidden = !useDots;

    if (!useDots) {
      // Starts part-way along (the first screenful) and fills with the last card.
      this.progressBar.style.transform = `scaleX(${(scrollLeft + clientWidth) / scrollWidth})`;
      return;
    }

    if (this.progressDots.childElementCount !== cards.length) {
      this.progressDots.replaceChildren(
        ...cards.map(() => {
          const dot = document.createElement('span');
          dot.className = 'carousel-progress__dot';
          return dot;
        })
      );
    }

    cards.forEach((active, i) => {
      this.progressDots.children[i].classList.toggle('is-active', active);
    });
  }
}

if (!customElements.get('carousel-arrows')) {
  customElements.define('carousel-arrows', CarouselArrows);
}
